import { useMemo, useCallback } from 'react';
import {
  Requisition,
  RequisitionsParams,
} from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { useAssociateRequisitions } from '@/components/shared/requisitions/associate-req/api/associateReqApi';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
interface UseRequisitionsOptionsParams {
  multiSelect: boolean;
  fallbackRequisitions?: Requisition[];
  selectedRequisitions?: Requisition[];
  offerId?: string;
  offerReq?: Requisition[];
}

const transformRequisitionToJobOption = (req: Requisition) => {
  return {
    value: req.id,
    label: `${req.code} - ${req.role_name}`,
    status: req.status,
    department: req.ats_department?.name,
    locations: req.ats_offices?.map((office) => office.name) || [],
    ats_job: req.ats_job,
    requisition_association: req.requisition_association,
    requisition_type: req.requisition_type,
    confidential: req.confidential,
    requisition_group_id: req.requisition_group_id,
    group_size: req.group_size,
    code: req.code,
  };
};

/**
 * Filters requisitions for single-select mode
 */
export const filterRequisitionsForSingleSelect = (
  options: any[],
  jobId?: string,
  offerId?: string
) => {
  return options.filter((req: any) => {
    const jobMatches =
      !req.ats_job || String(req.ats_job?.id) === String(jobId);
    const associationMatches =
      !req.requisition_association ||
      String(req.requisition_association?.offer?.id) === String(offerId);
    const isAtsApplication =
      req.requisition_association?.associable_type === 'AtsApplication';
    return jobMatches && associationMatches && !isAtsApplication;
  });
};

/**
 * Sorts requisitions for single-select mode
 * Priority: 1) Matching job, 2) No job, 3) Others
 */
export const sortRequisitionsForSingleSelect = (
  options: any[],
  jobId?: string
) => {
  return [...options].sort((a: any, b: any) => {
    const aMatchesJob = a.ats_job && String(a.ats_job.id) === String(jobId);
    const bMatchesJob = b.ats_job && String(b.ats_job.id) === String(jobId);
    const aHasNoJob = !a.ats_job;
    const bHasNoJob = !b.ats_job;

    if (aMatchesJob && bMatchesJob) return 0;
    if (aHasNoJob && bHasNoJob) return 0;

    if (aMatchesJob) return -1;
    if (bMatchesJob) return 1;

    if (aHasNoJob) return -1;
    if (bHasNoJob) return 1;

    return 0;
  });
};

/**
 * Sorts requisitions for multi-select mode
 * Priority: Current user's requisitions first
 */
export const sortRequisitionsForMultiSelect = (
  options: any[],
  currUserId?: number
) => {
  return [...options].sort((a, b) => {
    const aMatchesRecruiter = a.recruiter?.id === currUserId;
    const bMatchesRecruiter = b.recruiter?.id === currUserId;

    if (aMatchesRecruiter && !bMatchesRecruiter) return -1;
    if (!aMatchesRecruiter && bMatchesRecruiter) return 1;
    return 0;
  });
};

/**
 * Applies filtering and sorting to requisition options
 */
export const filterAndSortRequisitionOptions = (
  options: any[],
  {
    multiSelect,
    jobId,
    offerId,
    currUserId,
  }: {
    multiSelect: boolean;
    jobId?: string;
    offerId?: string;
    currUserId?: number;
  }
) => {
  if (multiSelect) {
    return sortRequisitionsForMultiSelect(options, currUserId);
  } else {
    const filtered = filterRequisitionsForSingleSelect(options, jobId, offerId);
    return sortRequisitionsForSingleSelect(filtered, jobId);
  }
};

export const useRequisitionsOptions = ({
  multiSelect,
  fallbackRequisitions = [],
  selectedRequisitions = [],
  offerId,
  offerReq = [],
}: UseRequisitionsOptionsParams) => {
  const { jobId } = useParams();
  const currUser = useSelector(selectCurrUser);
  const apiParams = useMemo<RequisitionsParams>(() => {
    const params: RequisitionsParams = {
      sort_key: 'created_at',
      sort_order: 'desc',
    };

    if (multiSelect) {
      params.status = {
        is: [
          'approved',
          'created',
          'open',
          'draft',
          'pending_approval',
          'rejected',
        ],
      };
      params.exclude_associated = true;
    } else {
      params.status = {
        is: ['approved', 'created', 'open', 'filled'],
      };
    }

    return params;
  }, [multiSelect]);

  const {
    data: requisitionsDets,
    isLoading: isLoadingRequisitions,
    refetch: refetchRequisitions,
  } = useAssociateRequisitions(apiParams);

  const requisitionsData = useMemo(() => {
    const combined = [...(requisitionsDets?.requisitions || []), ...offerReq];
    const seenIds = new Set<number>();
    return combined.filter(
      (req) => !seenIds.has(req.id) && seenIds.add(req.id)
    );
  }, [requisitionsDets?.requisitions, offerReq]);
  const availableRequisitions = useMemo<Requisition[]>(() => {
    if (requisitionsData && requisitionsData.length > 0) {
      return requisitionsData;
    }
    return fallbackRequisitions || [];
  }, [requisitionsData, fallbackRequisitions]);

  const requisitionsOptions = useMemo(() => {
    if (!availableRequisitions || availableRequisitions.length === 0) {
      return [];
    }

    const baseOptions = availableRequisitions.map((req) => ({
      ...transformRequisitionToJobOption(req),
      recruiter: req.recruiter,
      allRequisitions: availableRequisitions,
    }));

    return filterAndSortRequisitionOptions(baseOptions, {
      multiSelect,
      jobId,
      offerId,
      currUserId: currUser?.id,
    });
  }, [availableRequisitions, jobId, multiSelect, offerId, currUser?.id]);

  const selectedJobOptions = useMemo(() => {
    if (!selectedRequisitions || selectedRequisitions.length === 0) {
      return null;
    }

    const options = selectedRequisitions.map((req) =>
      transformRequisitionToJobOption(req)
    );

    return multiSelect ? options : options[0] || null;
  }, [selectedRequisitions, multiSelect]);

  const createHandleSelectChange = useCallback(
    (setSelectedRequisitions: (requisitions: Requisition[]) => void) =>
      (selectedOptions: any | any[] | null) => {
        if (!selectedOptions) {
          setSelectedRequisitions([]);
          return;
        }

        const selectedIds = Array.isArray(selectedOptions)
          ? selectedOptions.map((opt) => opt.value)
          : [selectedOptions.value];

        const newSelectedRequisitions = availableRequisitions.filter((req) =>
          selectedIds.includes(req.id)
        );

        setSelectedRequisitions(newSelectedRequisitions);
      },
    [availableRequisitions]
  );

  return {
    availableRequisitions,
    isLoadingRequisitions,
    requisitionsOptions,
    selectedJobOptions,
    createHandleSelectChange,
    refetchRequisitions,
  };
};
